import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { MessageSquareQuote, CheckCircle, Clock, Image } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDate } from "@/lib/utils";
import testimonialService from "@/services/testimonialService";

const PAGE_LIMIT = 10;

/**
 * Panel de testimonios recibidos (HU-20): los deja la postventa de clientes
 * B2B cerrados. Solo lectura — la publicación en el sitio es una curación
 * aparte, así que aquí se revisan antes de decidir qué se publica.
 */
const AdminTestimonios = () => {
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-testimonials", page],
    queryFn: () => testimonialService.getTestimonials({ page, limit: PAGE_LIMIT }),
    staleTime: 30 * 1000,
  });

  const testimonials = data?.testimonials ?? [];
  const pagination = data?.pagination;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="font-playfair font-bold text-3xl text-brown-dark">
          Testimonios
        </h1>
        <p className="text-brown-light">
          Testimonios recibidos en la postventa, para revisar antes de publicar.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total</CardTitle>
            <MessageSquareQuote className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-brown-dark">
              {pagination?.total ?? 0}
            </div>
            <p className="text-xs text-muted-foreground">Recibidos</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Autorizan publicar</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-brown-dark">
              {testimonials.filter((t) => t.allowPublish).length}
            </div>
            <p className="text-xs text-muted-foreground">Listos para la web</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pendientes</CardTitle>
            <Clock className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-brown-dark">
              {testimonials.filter((t) => !t.allowPublish).length}
            </div>
            <p className="text-xs text-muted-foreground">Pendientes de permiso</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Con prueba</CardTitle>
            <Image className="h-4 w-4 text-primary-dark" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-brown-dark">
              {testimonials.filter((t) => t.proofImageUrl).length}
            </div>
            <p className="text-xs text-muted-foreground">Captura adjunta</p>
          </CardContent>
        </Card>
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {pagination ? `${pagination.total} testimonio(s)` : "Testimonios"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[0, 1, 2].map((row) => (
                <Skeleton key={row} className="h-12 w-full" />
              ))}
            </div>
          ) : testimonials.length === 0 ? (
            <p className="text-brown-light py-8 text-center">
              Todavía no hay testimonios recibidos.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Testimonio</TableHead>
                    <TableHead>Publicación</TableHead>
                    <TableHead>Prueba</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {testimonials.map((testimonial) => (
                    <TableRow key={testimonial._id}>
                      <TableCell className="whitespace-nowrap text-sm">
                        {formatDate(testimonial.createdAt)}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-brown-dark">
                          {testimonial.name || "Sin nombre"}
                        </div>
                        {testimonial.company && (
                          <div className="text-xs text-brown-light">
                            {testimonial.company}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="max-w-md text-sm">
                        {testimonial.text}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={testimonial.allowPublish ? "default" : "secondary"}
                        >
                          {testimonial.allowPublish ? "Autoriza" : "Sin autorizar"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {testimonial.proofImageUrl &&
                        /^https?:\/\//i.test(testimonial.proofImageUrl) ? (
                          <a
                            className="text-primary-dark underline"
                            href={testimonial.proofImageUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Ver imagen
                          </a>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {pagination && pagination.pages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <span className="text-sm text-brown-light">
                Página {pagination.page} de {pagination.pages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page <= 1}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page >= pagination.pages}
                  onClick={() => setPage((current) => current + 1)}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminTestimonios;
